import React from 'react';
import { motion } from 'framer-motion';
import {
  Activity, CalendarDays, CalendarRange, ArrowRight, ArrowUpRight, ArrowDownRight,
  TrendingUp, AlertTriangle, Minus
} from 'lucide-react';

const PatternSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
    {[...Array(4)].map((_, i) => (
      <div key={`pattern-skeleton-${i}`} className="bg-slate-100 rounded-xl h-48 animate-pulse"></div>
    ))}
  </div>
);

const DayOfWeekCard = ({ data }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
  >
    <div className="flex items-center gap-2 mb-3">
      <CalendarDays className="h-5 w-5 text-blue-500" />
      <h4 className="font-semibold text-slate-800">Day of Week</h4>
    </div>
    {data.peak_day && (
      <div className="bg-blue-50 rounded-lg p-3 mb-3">
        <p className="text-2xl font-bold text-blue-700">{data.peak_day}</p>
        <p className="text-sm text-blue-600">Peak day at {data.peak_percent}% premium</p>
      </div>
    )}
    <div className="flex justify-between items-end h-16">
      {data.data?.map((d) => (
        <div key={d.day_name} className="flex flex-col items-center">
          <div
            className="w-6 rounded-t transition-all"
            style={{
              height: `${Math.max(d.premium_percent * 2, 4)}px`,
              backgroundColor: d.day_name === data.peak_day ? '#3b82f6' : '#cbd5e1'
            }}
          />
          <span className="text-[10px] text-slate-500 mt-1">{d.day_name?.substring(0, 2)}</span>
        </div>
      ))}
    </div>
    {data.insight && (
      <p className="text-xs text-slate-500 mt-3 border-t border-slate-100 pt-2">{data.insight}</p>
    )}
  </motion.div>
);

const WeekOfMonthCard = ({ data }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.1 }}
    className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
  >
    <div className="flex items-center gap-2 mb-3">
      <CalendarRange className="h-5 w-5 text-purple-500" />
      <h4 className="font-semibold text-slate-800">Week of Month</h4>
    </div>
    {data.peak_week && (
      <div className="bg-purple-50 rounded-lg p-3 mb-3">
        <p className="text-2xl font-bold text-purple-700">{data.peak_week}</p>
        <p className="text-sm text-purple-600">Highest at {data.peak_percent}% premium</p>
      </div>
    )}
    <div className="space-y-2">
      {data.data?.map((w) => (
        <div key={w.week_of_month} className="flex items-center gap-2">
          <span className="text-xs text-slate-500 w-14">{w.week_of_month}</span>
          <div className="flex-1 bg-slate-100 rounded-full h-2">
            <div
              className="h-2 rounded-full transition-all"
              style={{
                width: `${Math.min(w.premium_percent * 4, 100)}%`,
                backgroundColor: w.week_of_month === data.peak_week ? '#8b5cf6' : '#c4b5fd'
              }}
            />
          </div>
          <span className="text-xs font-medium text-slate-600 w-10 text-right">{w.premium_percent}%</span>
        </div>
      ))}
    </div>
  </motion.div>
);

const BuildupCard = ({ data }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.2 }}
    className={`bg-white rounded-xl border p-4 hover:shadow-md transition-shadow ${
      data.alert ? 'border-red-300 bg-red-50/30' : 'border-slate-200'
    }`}
  >
    <div className="flex items-center gap-2 mb-3">
      {data.alert ? (
        <AlertTriangle className="h-5 w-5 text-red-500" />
      ) : (
        <TrendingUp className="h-5 w-5 text-green-500" />
      )}
      <h4 className="font-semibold text-slate-800">Trend Alert</h4>
    </div>
    <div className={`rounded-lg p-3 mb-3 ${data.alert ? 'bg-red-100' : 'bg-green-50'}`}>
      <p className={`text-2xl font-bold ${data.alert ? 'text-red-700' : 'text-green-700'}`}>
        {data.consecutive_increases}
      </p>
      <p className={`text-sm ${data.alert ? 'text-red-600' : 'text-green-600'}`}>
        consecutive weeks increasing
      </p>
    </div>
    <div className="flex items-end justify-between h-12 gap-1">
      {data.data?.map((w) => (
        <div
          key={w.week_start}
          className="flex-1 rounded-t transition-all"
          style={{
            height: `${Math.max(w.premium_percent * 2.5, 8)}px`,
            backgroundColor: w.change > 0 ? '#ef4444' : w.change < 0 ? '#22c55e' : '#94a3b8'
          }}
          title={`${w.week_start}: ${w.premium_percent}%`}
        />
      ))}
    </div>
    <p className="text-xs text-slate-500 mt-3 border-t border-slate-100 pt-2">
      {data.insight}
    </p>
  </motion.div>
);

const PeriodComparisonCard = ({ data }) => {
  const directionBg = data.change.direction === 'up'
    ? 'bg-red-50' : data.change.direction === 'down' ? 'bg-green-50' : 'bg-slate-50';
  const valueColor = data.change.direction === 'up'
    ? 'text-red-700' : data.change.direction === 'down' ? 'text-green-700' : 'text-slate-700';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-center gap-2 mb-3">
        <ArrowRight className="h-5 w-5 text-amber-500" />
        <h4 className="font-semibold text-slate-800">This vs Last</h4>
      </div>
      <div className={`rounded-lg p-3 mb-3 ${directionBg}`}>
        <div className="flex items-center gap-2">
          {data.change.direction === 'up' ? (
            <ArrowUpRight className="h-6 w-6 text-red-500" />
          ) : data.change.direction === 'down' ? (
            <ArrowDownRight className="h-6 w-6 text-green-500" />
          ) : (
            <Minus className="h-6 w-6 text-slate-500" />
          )}
          <span className={`text-2xl font-bold ${valueColor}`}>
            {data.change.premium_percent_change > 0 ? '+' : ''}
            {data.change.premium_percent_change}%
          </span>
        </div>
        <p className="text-sm text-slate-600 mt-1">vs prior 4 weeks</p>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="bg-slate-50 rounded p-2">
          <p className="text-slate-500">Current</p>
          <p className="font-semibold text-slate-700">{data.current.premium_percent}%</p>
        </div>
        <div className="bg-slate-50 rounded p-2">
          <p className="text-slate-500">Prior</p>
          <p className="font-semibold text-slate-700">{data.prior.premium_percent}%</p>
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-3 border-t border-slate-100 pt-2">
        {data.insight}
      </p>
    </motion.div>
  );
};

export const PatternDetectionGrid = ({ patterns, loading }) => {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
          <Activity className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-800">Pattern Detection</h3>
          <p className="text-sm text-slate-500">Automatically detected patterns in your overtime data</p>
        </div>
      </div>

      {loading ? (
        <PatternSkeleton />
      ) : patterns ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {patterns.day_of_week && <DayOfWeekCard data={patterns.day_of_week} />}
          {patterns.week_of_month && <WeekOfMonthCard data={patterns.week_of_month} />}
          {patterns.buildup && <BuildupCard data={patterns.buildup} />}
          {patterns.period_comparison && <PeriodComparisonCard data={patterns.period_comparison} />}
        </div>
      ) : (
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-8 text-center">
          <Activity className="h-8 w-8 text-slate-400 mx-auto mb-2" />
          <p className="text-slate-600 font-medium">No patterns detected</p>
          <p className="text-sm text-slate-500 mt-1">Try adjusting your filters or date range</p>
        </div>
      )}
    </div>
  );
};
